import React, {Component, cloneElement} from 'react'
import PropTypes from 'prop-types'
import DropdownItems from "./DropdownItems";
import DropdownItem from "./DropdownItem";
import styles from './Dropdown.module.scss';
import WindowEventWatcher from "../WindowEventWatcher";
import OutsideClickWatcher from "../OutsideClickWatcher";
import {refBlur, refFocus, refSelectAll, replacements} from "../../../tools/tools";
import shiftAware from "../../../hoc/ShiftAware/ShiftAware";
import animations from '../../../assets/scss/animations/animate.module.scss'

const EXCEPTION_WORDS_ON_FILTER = ['airport', 'aeroporto', 'international', 'internacional'];

class Dropdown extends Component {

    static CHILDREN_ANCHOR = 'ChildrenAnchor';

    constructor(props) {
        super(props);

        this.state = {
            anchor: null,
            open: false,
            keyword: '',
            writingMode: false,
            animationClasses: '',
            optionsFiltered: [...props.options]
        };

        this.mounted = false;
        this.itemRefs = [];
        this.itemsContainerRef = React.createRef();
        this.localContainerRef = React.createRef();
    }

    static getDerivedStateFromProps(nextProps, prevState) {
        if (!prevState.writingMode) {
            const nextState = {optionsFiltered: nextProps.options};
            if (nextProps.setKeyboardAnchorOnCurrentValueAlways && !prevState.anchor) {
                nextState.anchor = nextProps.value;
            }

            if (prevState.anchor && prevState.anchor.code
                && !prevState.optionsFiltered.filter(option => option.code === prevState.anchor.code).length) {
                nextState.anchor = null;
            }

            nextState.keyword = nextProps.value && nextProps.value.hasOwnProperty('label') ? nextProps.value.label : '';
            return nextState;
        }
        return null;
    }

    hasSameIds = (op1, op2) => {
        const {optionsIdentifier} = this.props;
        return !!(op1 && op2 &&
            op1.hasOwnProperty(optionsIdentifier) &&
            op2.hasOwnProperty(optionsIdentifier) &&
            op1[optionsIdentifier] === op2[optionsIdentifier]);
    };

    recenterItemsVertically = () => {
        const {anchor, optionsFiltered} = this.state;
        const anchorIdx = optionsFiltered.findIndex(opt => this.hasSameIds(anchor, opt));
        if (anchorIdx >= 3) {
            //this.itemRefs[anchorIdx-3].current.scrollIntoView(); //scrollPageTo
            this.itemsContainerRef.current.scrollTop = this.itemRefs[anchorIdx - 3].current.offsetTop;
        }
    };

    filteredOptionsWithMaxResults = () => {
        const {optionsFiltered, keyword} = this.state;
        const {optionsMax, searchable, showOptionsBeforeType, stickCurrentValueOnTop, value} = this.props;

        if ((searchable && !showOptionsBeforeType && !keyword) || (!optionsFiltered)) {
            return [];
        }

        if (stickCurrentValueOnTop && value) {
            const currentValueIdx = optionsFiltered.findIndex(opt => this.hasSameIds(value, opt));
            if (currentValueIdx !== -1) {
                optionsFiltered.splice(currentValueIdx, 1);
            }
            optionsFiltered.unshift(value);
        }

        if (!optionsMax) {
            return optionsFiltered;
        }

        return optionsFiltered.slice(0, optionsMax);
    };

    navigateUp = (step = 1) => {
        const {children} = this.props;
        const {anchor} = this.state;
        const optionsFiltered = this.filteredOptionsWithMaxResults();
        let anchorIdx = optionsFiltered.findIndex(opt => this.hasSameIds(anchor, opt));
        if (anchor === Dropdown.CHILDREN_ANCHOR) {
            anchorIdx = optionsFiltered.length;
        }

        if (anchorIdx > 0) {
            const prevAnchor = optionsFiltered[anchorIdx - step];
            this.setState({anchor: prevAnchor}, this.recenterItemsVertically);
        } else if (children) {
            this.setState({anchor: Dropdown.CHILDREN_ANCHOR});
        } else if (optionsFiltered.length) {
            this.setState({anchor: optionsFiltered[optionsFiltered.length - 1]});
        }
    };

    navigateDown = (step = 1) => {
        const {children} = this.props;
        const {anchor} = this.state;
        const optionsFiltered = this.filteredOptionsWithMaxResults();
        const anchorIdx = optionsFiltered.findIndex(opt => this.hasSameIds(anchor, opt));

        if (anchorIdx < (optionsFiltered.length - 1)) {
            const nextAnchor = optionsFiltered[anchorIdx + step];
            this.setState({anchor: nextAnchor}, this.recenterItemsVertically);
        } else if (children) {
            this.setState({anchor: Dropdown.CHILDREN_ANCHOR});
        } else if (optionsFiltered.length) {
            this.setState({anchor: optionsFiltered[0]});
        }
    };

    handleArrowKeys = (e) => {
        if (!e) {
            return;
        }

        if (this.props.disabled) {
            if (['NumpadEnter', 'Enter'].indexOf(e.code) !== -1) this.setState({open: false});
            return null;
        }

        const code = e.code ? e.code : e.key;
        const {prevRef, nextRef, shiftPressed} = this.props;
        if (code === 'Tab' && shiftPressed) {
            this.onBlurLocalHandler(e);
            refFocus(prevRef);
            e.stopPropagation();
            e.preventDefault();
            return;
        }

        switch (code) {
            case 'Up':
            case 'ArrowUp':
                this.navigateUp();
                break;
            case 'Down':
            case 'ArrowDown':
                this.navigateDown();
                break;
            case 'NumpadEnter':
            case 'Enter':
            case 'Tab':
                const {anchor} = this.state;
                if (anchor === Dropdown.CHILDREN_ANCHOR) {
                    const {children} = this.props;
                    if (children && children.props && typeof children.props.onClick === 'function') {
                        children.props.onClick();
                    }
                    break;
                }

                if (code === 'Tab' && !anchor) {
                    this.onBlurLocalHandler(e);
                    refFocus(nextRef);
                }

                this.onSelectHandler(anchor, e);
                break;
            case 'Left':
            case 'ArrowLeft':
                this.onBlurLocalHandler(e);
                refFocus(prevRef);
                break;
            case 'Right':
            case 'ArrowRight':
                this.onBlurLocalHandler(e);
                refFocus(nextRef);
                break;
            case 'Escape':
                this.onBlurLocalHandler(e);
                return;
            default:
                return;
        }

        e.stopPropagation();
        e.preventDefault();
    };

    onTypeHandler = (e) => {
        const {onTypingHandler, optionsFilterFn, optionsSortFn, options, containerRef} = this.props;
        let {value} = containerRef ? containerRef.current : this.localContainerRef.current;
        //value = value.replace(/[^a-zA-Z0-9 ]+/g, '');

        const optionsFiltered = optionsSortFn(options.filter(opt => optionsFilterFn(opt, value)), value);
        const firstAnchor = optionsFiltered.length ? optionsFiltered[0] : null;
        this.setState(
            {optionsFiltered: optionsFiltered, anchor: firstAnchor, keyword: value, writingMode: true},
            () => onTypingHandler(value, e)
        );
    };

    onSelectHandler = (option, e) => {
        const modifier = {};

        if (e && e.hasOwnProperty('type') && e.type === 'click') modifier.anchor = option;

        if (option !== null) {
            const {containerRef} = this.props;
            refBlur(containerRef, this.localContainerRef);
            this.setState({open: false, writingMode: false, ...modifier}, () => {
                this.props.onChange(option, e);
            });
        }
    };

    onFocusLocalHandler = (e) => {
        const {wasClosedJustNow} = this.state;
        if (wasClosedJustNow) return;
        const {onFocusHandler, containerRef} = this.props;
        this.setState({open: true, animationClasses: `${animations.animated} ${animations.touchEffect}`}, () => {
            setTimeout(() => this.setState({animationClasses: ''}), 500);
            refFocus(containerRef, this.localContainerRef);
            refSelectAll(containerRef, this.localContainerRef);
            onFocusHandler(e);
        });
    };

    onBlurLocalHandler = (e) => {
        const {onBlurHandler, containerRef, value} = this.props;
        const val = typeof value === 'string' ? value : value && value.label ? value.label : '';
        this.setState({open: false, writingMode: false, keyword: val, wasClosedJustNow: true}, () => {
            refBlur(containerRef, this.localContainerRef);
            onBlurHandler(e);
            setTimeout(() => this.setState({wasClosedJustNow: false}), 250);
        });
    };

    renderHeader = () => {
        const {disabled, value, searchable, placeholder, containerRef, styledLabelValue, styledLabelMaxChars, animated, open} = this.props;
        const contRef = containerRef ? containerRef : this.localContainerRef;
        const {keyword, animationClasses} = this.state;
        const hasLabel = value && value.hasOwnProperty('label');
        const label = hasLabel ? value.label : placeholder;

        const classes = [styles.header];
        const inputClasses = [styles.input];
        if (disabled) classes.push(styles.disabledHeader);
        if (open) classes.push(styles.focused);
        if (!hasLabel && !searchable) classes.push(styles.labelPlaceholderStyle);
        if (!searchable) classes.push(styles.notSearchable);
        if (styledLabelValue && value && value.label) inputClasses.push(styles.hasValue);
        if (animated) classes.push(animationClasses);

        if (!searchable) {
            return (
                <div className={classes.join(' ')}
                     tabIndex={0}
                     onClick={this.onFocusLocalHandler}
                     onFocus={this.onFocusLocalHandler}
                     title={label}
                     role={'form'}
                     ref={contRef}>
                    {label}
                </div>
            );
        }

        let spanHover = '';
        const maxLetters = styledLabelMaxChars;
        const keywordSplit = keyword.split(' ');
        if (styledLabelValue && keywordSplit.length > 1) {
            const lastWord = keywordSplit[keywordSplit.length - 1];
            const firstWords = keywordSplit.slice(0, -1).join(' ').replace(/\s*$/, '');
            spanHover = (
                <span className={styles.spanHover} onClick={this.onFocusLocalHandler}>
                    {firstWords.length > maxLetters ? firstWords.slice(0, maxLetters) + '..' : firstWords}<span> {lastWord}</span>
                </span>
            );
        }

        return (
            <div className={classes.join(' ')}>
                <input ref={contRef}
                       id={placeholder}
                       aria-label={placeholder}
                       aria-required="true"
                       tabIndex={0}
                       disabled={disabled}
                       className={inputClasses.join(' ')}
                       onChange={this.onTypeHandler}
                       placeholder={placeholder}
                       name={placeholder}
                       onClick={this.onFocusLocalHandler}
                       onFocus={this.onFocusLocalHandler}
                       title={keyword}
                       value={keyword}
                       autoComplete={'off'}
                />
                {spanHover}
            </div>
        );
    };

    renderOptions = () => {
        const {value, disabled, children, boldTypingOccurrences, modifierItemsContainerClass} = this.props;
        const {anchor, open, keyword} = this.state;

        if (!open) {
            return null;
        }

        this.itemRefs = [];
        const optionsToRender = this.filteredOptionsWithMaxResults();
        const renderedOpts = optionsToRender.map((option, idx) => {
            const currentRef = React.createRef();
            this.itemRefs.push(currentRef);
            const selected = this.hasSameIds(value, option);
            const isAnchor = this.hasSameIds(anchor, option);
            const innerHTML = (
                <span dangerouslySetInnerHTML={
                    {__html: option.label.replace(new RegExp('(' + keyword + ')', 'ig'), `<b>$1</b>`)}
                }/>
            );
            const label = boldTypingOccurrences ? innerHTML : option.label;
            const isDisabled = disabled || (option.hasOwnProperty('disabled') && option.disabled);

            return (
                <DropdownItem containerRef={currentRef}
                              key={idx}
                              anchor={isAnchor}
                              disabled={isDisabled}
                              selected={selected}
                              onClick={isDisabled ? () => null : (e) => this.onSelectHandler(option, e)}>
                    {label}
                </DropdownItem>
            );
        });

        const modifiers = {};
        if (anchor === Dropdown.CHILDREN_ANCHOR) modifiers.selected = true;
        const childrenRender = children ? cloneElement(children, modifiers) : null;

        return (
            <OutsideClickWatcher handleClickOutside={this.onBlurLocalHandler}>
                <DropdownItems modifierClass={modifierItemsContainerClass}
                               disabled={disabled}
                               open={open}
                               containerRef={this.itemsContainerRef}>
                    {renderedOpts}
                    {childrenRender}
                </DropdownItems>
            </OutsideClickWatcher>
        );
    };

    render() {
        const {open} = this.state;
        const classes = [styles.container];
        if (open) classes.push(styles.focused);

        return (
            <div className={classes.join(' ')}>
                {this.renderHeader()}
                {this.renderOptions()}
                <WindowEventWatcher listening={open} eventName='keydown' handlerFn={this.handleArrowKeys}/>
            </div>
        );
    }
}

Dropdown.propTypes = {
    stickCurrentValueOnTop: PropTypes.bool,
    styledLabelValue: PropTypes.bool,
    styledLabelMaxChars: PropTypes.number,
    modifierItemsContainerClass: PropTypes.string,
    children: PropTypes.any,
    disabled: PropTypes.bool,
    containerRef: PropTypes.any,
    prevRef: PropTypes.any,
    nextRef: PropTypes.any,
    searchable: PropTypes.bool,
    placeholder: PropTypes.string,
    boldTypingOccurrences: PropTypes.bool,
    showOptionsBeforeType: PropTypes.bool,
    setKeyboardAnchorOnCurrentValueAlways: PropTypes.bool,
    optionsIdentifier: PropTypes.string,
    optionsMax: PropTypes.number,
    options: PropTypes.array,
    optionsFilterFn: PropTypes.func,
    onFocusHandler: PropTypes.func,
    onBlurHandler: PropTypes.func,
    onChange: PropTypes.func,
    onTypingHandler: (props, propName, componentName) => {
        if ((props.searchable && typeof (props[propName]) != 'function')) {
            return new Error(`[${componentName}]: The attribute '${propName}={function}' is required when the searchable attribute is ON. You have to filter your own options at parent component.`);
        }
    },
    value: (props, propName, componentName) => {
        if ((props.searchable && props[propName] && typeof (props[propName]['label']) != 'string')) {
            return new Error(`[${componentName}]: The attribute '${propName}={object{value, label}}' label is required to be string because it will be written on the search input DOM that only accept strings.`);
        }
    },
    shiftPressed: PropTypes.bool,
    animated: PropTypes.bool,
};

Dropdown.defaultProps = {
    stickCurrentValueOnTop: false,
    styledLabelValue: false,
    styledLabelMaxChars: 200,
    modifierItemsContainerClass: '',
    children: null,
    disabled: false,
    containerRef: null,
    prevRef: null,
    nextRef: null,
    searchable: false,
    placeholder: 'Select...',
    boldTypingOccurrences: false,
    showOptionsBeforeType: true,
    setKeyboardAnchorOnCurrentValueAlways: false,
    optionsIdentifier: 'id',
    optionsMax: null,
    options: [],
    optionsFilterFn: (option, keyword = null, exceptionWords = EXCEPTION_WORDS_ON_FILTER) => {
        if (option === null || keyword === null) {
            return true;
        }

        //const ew = new RegExp(`(${exceptionWords.join('|')})`);
        keyword = keyword.toLowerCase();
        return (option.hasOwnProperty('value') && typeof option.value === 'string'
            && replacements(option.value.toLowerCase(), exceptionWords, '').includes(keyword))
            || (option.hasOwnProperty('code') && replacements(option.code.toLowerCase(), exceptionWords, '').startsWith(keyword))
            || (option.hasOwnProperty('code') && replacements(option.code.toLowerCase(), exceptionWords, '').includes(keyword))
            || (option.hasOwnProperty('name') && replacements(option.name.toLowerCase(), exceptionWords, '').split(' ')
                .some(w => w.startsWith(keyword)))
            || (option.hasOwnProperty('name') && replacements(option.name.toLowerCase(), exceptionWords, '').includes(keyword))
            || (option.hasOwnProperty('cityName') && replacements(option.cityName.toLowerCase(), exceptionWords, '').includes(keyword))
            || (option.hasOwnProperty('countryName') && replacements(option.countryName.toLowerCase(), exceptionWords, '').includes(keyword))
            || (option.hasOwnProperty('regionName') && replacements(option.regionName.toLowerCase(), exceptionWords, '').includes(keyword))
            || (option === keyword);
    },
    optionsSortFn: (
        list,
        keyword = null,
        sortByProvidedKey = null,
        exceptionWords = EXCEPTION_WORDS_ON_FILTER,
        options = {
            code: ['startsWith'],
            name: ['innerStartsWith', 'includes'],
            cityName: ['includes'],
            regionName: ['includes'],
            countryName: ['includes']
        },
    ) => {
        if (!Array.isArray(list) || !keyword || keyword === '') return list;//options;

        //const ew = new RegExp(`(${exceptionWords.join('|')})`);
        keyword = keyword.toLowerCase();

        const sortByProvidedKeyFn = (a, b) => {
            if (a && b && a.hasOwnProperty(sortByProvidedKey) && b.hasOwnProperty(sortByProvidedKey)) {
               return replacements(a[sortByProvidedKey].toLowerCase(), exceptionWords, '') - replacements(b[sortByProvidedKey].toLowerCase(), exceptionWords, '');
            }

            return a - b;
        };

        let opts = sortByProvidedKey ? [...list].sort(sortByProvidedKeyFn) : [...list];

        let resultOps = [];
        for (let prop of Object.keys(options)) {
            for (let rule of options[prop]) {
                // eslint-disable-next-line
                opts = opts.filter(o => !resultOps.includes(o));
                switch (rule) {
                    case 'includes':
                        resultOps = [...resultOps, ...[...opts].filter(
                            opt => replacements(opt.hasOwnProperty(prop) && opt[prop].toLowerCase(), exceptionWords, '').includes(keyword)
                        )];
                        break;
                    case 'startsWith':
                        resultOps = [...resultOps, ...[...opts].filter(
                            opt => replacements(opt.hasOwnProperty(prop) && opt[prop].toLowerCase(), exceptionWords, '').startsWith(keyword)
                        )];
                        break;
                    case 'innerStartsWith':
                        resultOps = [...resultOps, ...[...opts].filter(
                            opt => replacements(opt.hasOwnProperty(prop) && opt[prop].toLowerCase(), exceptionWords, '').split(' ')
                                .some(w => w.startsWith(keyword))
                        )];
                        break;
                    default:
                        break;
                }
            }
        }

        return resultOps;
    },
    onFocusHandler: () => null,
    onBlurHandler: () => null,
    onChange: () => null,
    onTypingHandler: () => null,
    value: '',
    shiftPressed: false,
    animated: false,
};

export default shiftAware(Dropdown);